import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { CardContent, Chip, Grid, List, ListItem, Paper, Typography } from '@material-ui/core'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { ParcelOrderDetailTable } from 'src/components/Table/detail/ParcelOrder'
import { ParcelOrderDialog } from 'src/modules/supply_chain/components/parcel/OrderDialog'
import { ParcelShipmentDialog } from 'src/modules/supply_chain/components/parcel/ShipmentDialog'
import { ParcelOrderUnitListTable } from 'src/components/Table/list/ParcelOrderUnit'
import { ParcelOrderBarcodedUnitListTable } from 'src/components/Table/list/ParcelOrderBarcodedUnit'
import { ParcelShipmentListTable } from 'src/components/Table/list/ParcelShipment'
import { EventListTable } from 'src/components/Table/list/Event'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { skuit } from 'src/services/skuit'
import { useQuery, UseQueryResult } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'
import { Facility, ParcelOrder, Tag } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

interface InventoryTabProps {
  order: UseQueryResult<ParcelOrder>
}

const InventoryTab = (props: InventoryTabProps) => {
  const { order } = props
  const { guid } = useParams<PathProps>()

  const shipmentsPaginator = useQueryPaginator(
    'parcel-order-shipment-list',
    skuit.sc.parcel.shipments.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'parcel-order-units-list',
    skuit.sc.parcel.orders.units.list,
    { params: { page_size: 5, orders: guid, ordering: 'status' } }
  )
  const barcodedPaginator = useQueryPaginator(
    'parcel-order-barcoded-units',
    skuit.sc.parcel.orders.barcodedUnits.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )

  return (
    <DetailTab>
      <Paper>
        <CardContent>
          <Typography variant="h5" component="h3">
            {'Facilities'}
          </Typography>
          {order.data?.facilities && order.data?.facilities?.length > 0 ? (
            <List>
              {order.data?.facilities?.map((item: Facility, index: number) => (
                <ListItem key={item.guid} component="a" href={`warehouse/facilities/${item.guid}`}>
                  {item.name}{' '}
                  {order.data.facilities && index !== order.data.facilities.length - 1 && ','}
                </ListItem>
              ))}{' '}
            </List>
          ) : (
            <Grid item container justify="center" xs={10}>
              <span>Nothing here yet.</span>
            </Grid>
          )}
        </CardContent>
      </Paper>
      <ParcelShipmentListTable
        paginator={shipmentsPaginator}
        fields={['trackingNumber', 'consignee', 'facility', 'carrier', 'status']}
      />
      <ParcelOrderUnitListTable
        paginator={unitsPaginator}
        title="Units"
        fields={['productName', 'product', 'sku', 'numUnits', 'fulfillmentStatus', 'status']}
      />
      <ParcelOrderBarcodedUnitListTable
        paginator={barcodedPaginator}
        fields={['unit', 'product']}
      />
    </DetailTab>
  )
}

const AttachmentsTab = () => {
  const { guid } = useParams<PathProps>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.parcel.orders.attachments.list(guid),
    {
      params: { page_size: 4, orders: guid },
    }
  )

  return (
    <DetailTab>
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={() => attachmentsPaginator.refresh()}
          addAttachment={(config) =>
            skuit.sc.parcel.orders.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <PaginatedFilePreviewGallery
        title="Attachments"
        paginator={attachmentsPaginator}
        getIdent={(item) => item.guid}
        onAdd={() => {
          setAddAttachmentDialogOpen(true)
        }}
        renderItem={(item) => {
          return (
            <>
              <GenericPreview url={item.file} />
              <div>
                {item.tags.map((tag: Tag) => (
                  <Chip key={tag.slug} label={tag.name} />
                ))}
              </div>
            </>
          )
        }}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Parcel Order Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

const IntegrationTab = () => {
  const { guid } = useParams<PathProps>()

  const sOrder = useQuery(['order-shipstation-integration', guid], () =>
    skuit.sc.integrations.shipstation.orders.retrieve(guid)
  )

  return (
    <DetailTab>
      <Paper>
        <CardContent>
          <Typography variant="h5" component="h3">
            Shipstation Integration
          </Typography>
          {sOrder.data ? (
            <List>
              <ListItem>
                Order Number: {sOrder.data.orderNumber}
              </ListItem>
              <ListItem>
                Shipstation ID: {sOrder.data.shipstationId}
              </ListItem>
              <ListItem
                component="a"
                href={`https://ship11.shipstation.com/orders/all-orders-search-result?quickSearch=${sOrder.data.orderNumber}`}
                target="_blank"
              >
                Link to Shipstation
              </ListItem>
            </List>
          ) : (
            <Grid item container justify="center" xs={10}>
              <span>Parcel Order not synced.</span>
            </Grid>
          )}
        </CardContent>
      </Paper>
    </DetailTab>
  )
}

export const OrderPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)
  const [shipmentCreateOpen, setShipmentCreateOpen] = useState(false)

  const order = useQuery(['order-details', guid], () => skuit.sc.parcel.orders.retrieve(guid))
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.sc.parcel.orders.customFieldValues.list,
    { params: { orders: guid } }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const refresh = () => {
    order.refetch()
  }

  return (
    <>
      {createOpen && (
        <ParcelOrderDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      {shipmentCreateOpen && (
        <ParcelShipmentDialog
          open={shipmentCreateOpen}
          handleClose={() => {
            setShipmentCreateOpen(false)
          }}
          order={order.data}
        />
      )}
      <DetailPage
        obj={order}
        title="Parcel Order"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Parcel Orders', url: '/parcel/orders' },
        ]}
        detailComponent={
          <ParcelOrderDetailTable obj={order} refresh={refresh} customFields={customFieldValues} />
        }
        secondaryComponent={
          <Workflow
            title="Order Workflow"
            loading={order.isFetching}
            workflow={order.data?.workflow || []}
            status={order.data?.workflowStatus || ''}
            actions={order.data?.getWorkflowActions(() => order.refetch()) || {}}
          />
        }
        menuItems={[
          {
            Icon: AddIcon,
            actions: [
              { title: 'New Parcel Order', onClick: () => setCreateOpen(true) },
              { title: 'Create Shipment from Order', onClick: () => setShipmentCreateOpen(true) },
            ],
          },
        ]}
        tabs={['Inventory', 'Attachments', 'Events', 'Integrations']}
      >
        <InventoryTab order={order} />
        <AttachmentsTab />
        <EventsTab />
        <IntegrationTab />
      </DetailPage>
    </>
  )
}
